import { DeviceCapabilities } from "./DeviceCapabilities.js";
/** Roles that may open a module. Mirrors `deviceDefaults` keys in the dine-config contract. */
export const OPS_ROLES = ["owner", "manager", "cashier", "kitchen", "kiosk", "board"];
/** One feature of the lumora-ops shell. Immutable. */
export class OpsModule {
    id;
    kind;
    roles;
    requires;
    constructor(init) {
        this.id = init.id;
        this.kind = init.kind;
        this.roles = new Set(init.roles);
        this.requires = new Set(init.requires ?? []);
        Object.freeze(this);
    }
    /** Role allows it and the device has every required capability. */
    allows(role, caps) {
        return this.roles.has(role) && caps.hasAll(this.requires);
    }
}
/** Ordered, immutable list of modules. Order is the display and tie-break order. */
export class OpsModuleRegistry {
    list;
    constructor(modules) {
        const seen = new Set();
        for (const m of modules) {
            if (seen.has(m.id))
                throw new Error(`duplicate OpsModule id: ${m.id}`);
            seen.add(m.id);
        }
        this.list = Object.freeze([...modules]);
        Object.freeze(this);
    }
    /** All modules in registry order. */
    all() {
        return this.list;
    }
    get(id) {
        return this.list.find((m) => m.id === id);
    }
    /**
     * Modules the user may open: role allows, device has every required capability, and the
     * Variant lists none (absent or empty) or includes the id. Unknown variant ids are ignored.
     * Result is in registry order.
     */
    enabledModules(role, caps, variantModules) {
        const only = variantModules && variantModules.length > 0 ? new Set(variantModules) : null;
        return Object.freeze(this.list.filter((m) => m.allows(role, caps) && (only === null || only.has(m.id))));
    }
    /** The built-in lumora-ops modules. */
    static builtIn = new OpsModuleRegistry([
        new OpsModule({ id: "counterHome", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "history", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "tipOut", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "timeclock", kind: "web", roles: ["owner", "manager", "cashier", "kitchen"] }),
        new OpsModule({ id: "kiosk", kind: "web", roles: ["kiosk"] }),
        new OpsModule({ id: "board", kind: "web", roles: ["owner", "manager", "board"] }),
        new OpsModule({ id: "kitchen", kind: "web", roles: ["owner", "manager", "kitchen"] }),
        new OpsModule({ id: "marketplaceEntry", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "drawerAudit", kind: "web", roles: ["owner", "manager"] }),
        new OpsModule({ id: "content", kind: "web", roles: ["owner", "manager"] }),
        new OpsModule({ id: "posOrder", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "onlineFeed", kind: "web", roles: ["owner", "manager", "cashier"] }),
        new OpsModule({ id: "ownerOrders", kind: "web", roles: ["owner", "manager"] }),
        new OpsModule({ id: "reports", kind: "web", roles: ["owner", "manager"] }),
        new OpsModule({ id: "readerSettings", kind: "native", roles: ["owner", "manager"] }),
        new OpsModule({ id: "devicePairing", kind: "native", roles: ["owner", "manager"] }),
    ]);
}
//# sourceMappingURL=OpsModule.js.map